/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataExplorerFilters } from './DataExplorerFilters';
import { dataExplorerSelectors, dataExplorerActions } from '../../store';
import type {
  DataExplorerModuleState,
  DiscoveryMetadataItems,
  Queryable,
  QueryableConfiguration,
} from '../../store/types';
import { useFetchItems } from '../../utils/dataexplorer-api/hooks';
import { useDataExplorerTranslation } from '../../utils/i18n';

export const DataExplorerFiltersConnect: React.FC = () => {
  const dispatch = useDispatch();

  const baseUrl = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getDiscoveryCatalogueURL(store),
  );

  const filters = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getQueryables(store),
  );

  const queryableConfigurations = useSelector(
    (store: DataExplorerModuleState) =>
      dataExplorerSelectors.getQueryableConfigurations(store),
  );

  // Use this to get the current filters in the hook without it being in the dependency array
  const filtersRef = useRef<Queryable[]>([]);

  const searchText = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getSearchFilter(store),
  );

  const concepts = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getConcepts(store),
  );

  const meteogateOnly = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getMeteogateOnly(store),
  );

  const showMeteoGateBranding = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getShowMeteoGateBranding(store),
  );

  const { data: filteringDatasets } = useFetchItems(
    baseUrl,
    searchText,
    [],
    concepts,
    undefined,
    meteogateOnly,
  );

  const { t } = useDataExplorerTranslation();

  const setFilterValue = (
    id: string,
    value: string,
    checked: boolean,
  ): void => {
    dispatch(dataExplorerActions.setQueryableValue({ id, value, checked }));
  };

  const handleMeteoGateToggle = (enabled: boolean): void => {
    dispatch(dataExplorerActions.setMeteogateOnly({ enabled }));
  };

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    const updateDisplayedFilters = (): void => {
      const queryables: Queryable[] = [];

      if (filteringDatasets) {
        // Update the list of possible values for each filter
        queryableConfigurations.forEach((config) => {
          const currentFilter = filtersRef.current.find(
            (filter) => filter.id === config.id,
          );
          const queryable = getQueryableWithValues(
            config,
            filteringDatasets,
            t,
            currentFilter,
          );
          queryables.push(queryable);
        });
      }

      dispatch(dataExplorerActions.setQueryables({ queryables }));
    };
    updateDisplayedFilters();
  }, [filteringDatasets, dispatch, queryableConfigurations, t]);

  return (
    <DataExplorerFilters
      filters={filters}
      meteoGateOnly={meteogateOnly}
      onSetFilterValue={setFilterValue}
      onToggleMeteoGateOnly={handleMeteoGateToggle}
      showMeteoGateBranding={showMeteoGateBranding}
    />
  );
};

// Go through datasets to get the possible values for the queryable filter
const getQueryableWithValues = (
  config: QueryableConfiguration,
  filteringDatasets: DiscoveryMetadataItems,
  t: (key: string) => string,
  currentFilter?: Queryable,
): Queryable => {
  const queryable: Queryable = {
    id: config.id,
    label: config.label,
    values: [],
    selected: [],
  };

  if (filteringDatasets) {
    filteringDatasets.features.forEach((feature) => {
      // Get the list of values of the property for the current feature
      let values = getNestedProperty(feature.properties, config.id);

      if (values !== undefined) {
        if (!Array.isArray(values)) {
          values = [values];
        }

        (values as unknown[]).forEach((value) => {
          if (value !== undefined) {
            const existingValue = queryable.values.find(
              (val) => val.value === (value as string),
            );
            if (!existingValue) {
              queryable.values.push({
                value: value as string,
                datasetCount: 1,
              });
            } else {
              // Count how many datasets have the value
              existingValue.datasetCount += 1;
            }
          }
        });
      }
    });
  }

  queryable.values = queryable.values.slice().sort((valueA, valueB) => {
    if (typeof valueA.value === 'string' && typeof valueB.value === 'string') {
      return t(valueA.value).localeCompare(t(valueB.value));
    }
    return -1;
  });

  // Set the selected value to same as previously selected value
  if (currentFilter) {
    const previouslySelected = currentFilter.selected;
    if (
      previouslySelected &&
      queryable.values.find((value) => previouslySelected.includes(value.value))
    ) {
      queryable.selected = previouslySelected;
    }
  }

  return queryable;
};

const getNestedProperty = (
  obj: Record<string, unknown> | unknown[],
  path: string,
): unknown => {
  const keys = path.split('.');
  let result: unknown = obj;

  for (const key of keys) {
    if (Array.isArray(result)) {
      result = result
        .map((item) =>
          getNestedProperty(
            item as unknown[],
            keys.slice(keys.indexOf(key)).join('.'),
          ),
        )
        .flat(Infinity);
      return result;
    }
    if (result && typeof result === 'object' && key in result) {
      result = (result as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }

  return result;
};
